'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import { View } from 'react-native-web';

/*
 * react-native-safe-area-context for the web. On a phone's installed app the notch and the home bar
 * eat into the page; the browser reports that through env(safe-area-inset-*), which is read once
 * here and handed to the app's screens as insets. On a computer it is all zeros.
 */
const ZERO = { top: 0, bottom: 0, left: 0, right: 0 };
const InsetsContext = createContext(ZERO);

function measure() {
  const probe = document.createElement('div');
  probe.style.cssText = 'position:fixed;visibility:hidden;pointer-events:none;padding:env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom) env(safe-area-inset-left)';
  document.body.appendChild(probe);
  const style = getComputedStyle(probe);
  const insets = { top: parseFloat(style.paddingTop) || 0, right: parseFloat(style.paddingRight) || 0, bottom: parseFloat(style.paddingBottom) || 0, left: parseFloat(style.paddingLeft) || 0 };
  probe.remove();
  return insets;
}

export function SafeAreaProvider({ children, style }) {
  const [insets, setInsets] = useState(ZERO);
  useEffect(() => {
    setInsets(measure());
    const onResize = () => setInsets(measure());
    window.addEventListener('orientationchange', onResize);
    return () => window.removeEventListener('orientationchange', onResize);
  }, []);
  return (
    <InsetsContext.Provider value={insets}>
      <View style={[{ flex: 1 }, style]}>{children}</View>
    </InsetsContext.Provider>
  );
}

export const useSafeAreaInsets = () => useContext(InsetsContext);
export const useSafeAreaFrame = () => ({ x: 0, y: 0, width: typeof window === 'undefined' ? 0 : window.innerWidth, height: typeof window === 'undefined' ? 0 : window.innerHeight });
export const SafeAreaInsetsContext = InsetsContext;

export function SafeAreaView({ children, style, edges, ...rest }) {
  const insets = useContext(InsetsContext);
  const all = !edges || edges.length === 0;
  const has = (edge) => all || edges.includes(edge);
  return (
    <View {...rest} style={[{ paddingTop: has('top') ? insets.top : 0, paddingBottom: has('bottom') ? insets.bottom : 0, paddingLeft: has('left') ? insets.left : 0, paddingRight: has('right') ? insets.right : 0 }, style]}>
      {children}
    </View>
  );
}
